import { financialToneClass } from "./financialTone";

const unavailable = "—";

function formatNumber(value, options = {}) {
  const number = Number(value);
  return Number.isFinite(number)
    ? number.toLocaleString("en-US", options)
    : unavailable;
}

function formatPrice(value) {
  const number = Number(value);
  if (!Number.isFinite(number) || number === 0) return unavailable;
  return number.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 5,
  });
}

function formatProfit(value) {
  const profit = Number(value);
  if (!Number.isFinite(profit)) return unavailable;
  const sign = profit >= 0 ? "+" : "-";
  return `${sign}$${Math.abs(profit).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function toFiniteNumber(value) {
  if (value === null || value === undefined || value === "") return null;
  const amount = Number(value);
  return Number.isFinite(amount) ? amount : null;
}

function nonEmptyString(value) {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

/**
 * MT5 exposes the open time as epoch seconds (`time`) and as epoch
 * milliseconds (`time_msc`); some publishers emit an ISO string instead.
 * Normalise all three to epoch milliseconds.
 */
function toEpochMs(value) {
  const numeric = toFiniteNumber(value);
  if (numeric !== null) {
    if (numeric <= 0) return null;
    return numeric > 1e11 ? numeric : numeric * 1000;
  }

  const text = nonEmptyString(value);
  if (!text) return null;
  const parsed = Date.parse(text);
  return Number.isFinite(parsed) ? parsed : null;
}

export function resolvePositionOpenedAt(position) {
  const candidates = [
    position.time,
    position.time_msc,
    position.time_open,
    position.open_time,
  ];

  for (const candidate of candidates) {
    const openedAt = toEpochMs(candidate);
    if (openedAt !== null) return openedAt;
  }
  return null;
}

/**
 * Ownership comes from fields MT5 itself controls. The generic `GoldBot-V1`
 * order comment is deliberately ignored: it cannot tell a bot position from a
 * manual one.
 */
export function resolvePositionStrategy(position) {
  const strategy =
    nonEmptyString(position.strategy_name) ||
    nonEmptyString(position.strategy_id);
  if (strategy) return strategy;

  const magic = toFiniteNumber(position.magic);
  if (magic === null) return unavailable;
  return magic === 0 ? "Manual" : "GoldBot";
}

function formatDuration(openedAtMs, now) {
  if (openedAtMs === null) return unavailable;

  const elapsedMs = now.getTime() - openedAtMs;
  // An open time in the future means the timestamp was misread. Say so rather
  // than clamping it to a plausible-looking 0m.
  if (elapsedMs < -60_000) return unavailable;

  const elapsedMinutes = Math.max(0, Math.floor(elapsedMs / 60000));
  const hours = Math.floor(elapsedMinutes / 60);
  const minutes = elapsedMinutes % 60;
  return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
}

function formatSide(direction) {
  const normalized = String(direction || "").toUpperCase();
  if (normalized === "BUY") return "Buy";
  if (normalized === "SELL") return "Sell";
  return "Unknown";
}

export function buildLivePositionRows(positions, now = new Date()) {
  return positions.map((position, index) => {
    return {
      id: position.ticket ?? `${position.symbol || "position"}-${index}`,
      symbol: position.symbol || unavailable,
      strategy: resolvePositionStrategy(position),
      side: formatSide(position.direction),
      lot: formatNumber(position.volume, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
      entry: formatPrice(position.price_open),
      current: formatPrice(position.price_current),
      sl: formatPrice(position.sl),
      tp: formatPrice(position.tp),
      profit: formatProfit(position.profit),
      // Colour follows the live result, never the side: a short in
      // profit is green exactly like a long in profit.
      profitTone: financialToneClass(position.profit),
      r: unavailable,
      duration: formatDuration(resolvePositionOpenedAt(position), now),
    };
  });
}

export function getPositionsTableState({
  data,
  error,
  isLoading,
  rowCount,
}) {
  if (isLoading) {
    return { eyebrow: "Loading positions", message: "Loading positions..." };
  }
  if (error) {
    return { eyebrow: "API unavailable", message: "Positions unavailable" };
  }
  if (data?.stale) {
    return {
      eyebrow: "Stale snapshot",
      message: rowCount === 0 ? "No open positions" : null,
    };
  }
  if (rowCount === 0) {
    return { eyebrow: "No open trades", message: "No open positions" };
  }
  return {
    eyebrow: `${rowCount} open ${rowCount === 1 ? "trade" : "trades"}`,
    message: null,
  };
}
